/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { ReactNode, useMemo } from 'react'

// react-chartjs-2 components
import Card from '@mui/material/Card'
import Icon from '@mui/material/Icon'
import { Bar } from 'react-chartjs-2'

import colors from 'assets/theme/base/colors'
import darkColors from 'assets/theme-dark/base/colors'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { useMaterialUIController } from 'context'
import configs from 'modules/Charts/BarCharts/VerticalBarChart/configs'
import LoadingComponent from 'modules/Loading'

// Declaring props types for HorizontalBarChart
interface Props {
	icon?: {
		color?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'light' | 'dark'
		component: ReactNode
	}
	title?: string
	description?: string | ReactNode
	height?: string | number
	chart: {
		labels: string[]
		datasets: {
			label: string
			color: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'light' | 'dark'
			data: number[]
		}[]
	}
	stacked?: boolean
	percentage?: boolean
	totals?: number[]
	[key: string]: any
}

// Only the highest non-empty segment of a stacked bar gets the rounded end, and no gap above it
const isTopOfStack = (ctx: any) =>
	!ctx.chart.data.datasets
		.slice(ctx.datasetIndex + 1)
		.some((dataset: any) => dataset.data[ctx.dataIndex] > 0)

function VerticalBarChart({
	icon,
	title,
	description,
	height,
	chart,
	stacked,
	percentage,
	totals,
}: Props): JSX.Element {
	const [{ darkMode }] = useMaterialUIController()
	const isChartValid = chart && chart.datasets && chart.datasets.length > 0

	const stackedSegmentStyle = stacked
		? {
				borderSkipped: 'bottom',
				borderColor: darkMode ? darkColors.background.card : colors.white.main,
				borderWidth: (ctx: any) =>
					isTopOfStack(ctx) ? 0 : { top: 2, right: 0, bottom: 0, left: 0 },
				borderRadius: (ctx: any) => (isTopOfStack(ctx) ? 4 : 0),
		  }
		: {}

	const chartDatasets = isChartValid
		? chart.datasets.map(dataset => ({
				...dataset,
				weight: 5,
				borderWidth: 0,
				borderRadius: 4,
				backgroundColor: colors[dataset.color]
					? colors[dataset.color || 'dark'].main
					: colors.dark.main,
				fill: false,
				maxBarThickness: 35,
				...stackedSegmentStyle,
		  }))
		: []

	const { data, options } = isChartValid
		? configs(chart.labels || [], chartDatasets, { stacked, percentage, totals })
		: { data: null, options: null }

	const chartRender = useMemo(
		() =>
			isChartValid ? (
				<MDBox height={height}>
					<Bar data={data} options={options} />
				</MDBox>
			) : (
				<LoadingComponent type="box" height={height} />
			),
		[chart, height, stacked, percentage, totals, darkMode]
	)
	const renderChart = (
		<MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
			{title || description ? (
				<MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
					{icon.component && (
						<MDBox
							width="4rem"
							height="4rem"
							bgColor={icon.color || 'info'}
							variant="gradient"
							coloredShadow={icon.color || 'info'}
							borderRadius="xl"
							display="flex"
							justifyContent="center"
							alignItems="center"
							color="white"
							mt={-5}
							mr={2}
						>
							<Icon fontSize="medium">{icon.component}</Icon>
						</MDBox>
					)}
					<MDBox mt={icon.component ? -2 : 0}>
						{title && <MDTypography variant="h6">{title}</MDTypography>}
						<MDBox mb={2}>
							<MDTypography component="div" variant="button" color="text">
								{description}
							</MDTypography>
						</MDBox>
					</MDBox>
				</MDBox>
			) : null}
			{chartRender}
		</MDBox>
	)

	return title || description ? <Card>{renderChart}</Card> : renderChart
}

// Declaring default props for VerticalBarChart
VerticalBarChart.defaultProps = {
	icon: { color: 'info', component: '' },
	title: '',
	description: '',
	height: '19.125rem',
	stacked: false,
	percentage: false,
	totals: undefined,
}

export default VerticalBarChart
